import { SceneObject, Behavior } from '../types';

// Composite figures: a root primitive with child primitives parented to it.
// Children store LOCAL offsets (relative to the root), so moving the root
// moves the whole figure as one. Each instantiation gets fresh unique ids.

export interface FigureDef {
  id: string;
  label: string;
  build: () => SceneObject[];
}

function uid(): string {
  const c = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `id_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

interface PartSpec {
  type: SceneObject['type'];
  name: string;
  pos: [number, number, number]; // local offset from the root
  color: string;
  scale?: [number, number, number];
  behaviors?: Behavior[];
}

// Assemble a figure: part[0] is the root (top-level, world position `at`),
// every other part becomes a child with a local offset.
function assemble(rootName: string, at: [number, number, number], parts: PartSpec[]): SceneObject[] {
  const rootId = uid();
  return parts.map((p, i) => {
    const isRoot = i === 0;
    const pos = isRoot
      ? { x: at[0] + p.pos[0], y: at[1] + p.pos[1], z: at[2] + p.pos[2] }
      : { x: p.pos[0], y: p.pos[1], z: p.pos[2] };
    return {
      uuid: isRoot ? rootId : uid(),
      name: isRoot ? rootName : `${rootName} ${p.name}`,
      type: p.type,
      position: pos,
      rotation: { x: 0, y: 0, z: 0 },
      scale: p.scale
        ? { x: p.scale[0], y: p.scale[1], z: p.scale[2] }
        : { x: 1, y: 1, z: 1 },
      color: p.color,
      parentId: isRoot ? undefined : rootId,
      behaviors: p.behaviors ?? [],
    };
  });
}

export const FIGURES: FigureDef[] = [
  {
    id: 'hero',
    label: 'Hero',
    build: () =>
      assemble('Hero', [0, 0.6, 4], [
        // root = body, tagged Player so the whole figure is controllable
        { type: 'cube', name: 'body', pos: [0, 0, 0], color: '#2B8CFF', scale: [0.8, 1.1, 0.5], behaviors: [{ type: 'player', params: { speed: 6 } }] },
        { type: 'sphere', name: 'head', pos: [0, 0.95, 0], color: '#FFD9B3', scale: [0.55, 0.55, 0.55] },
        { type: 'cube', name: 'arm L', pos: [-0.6, 0.05, 0], color: '#2476D6', scale: [0.25, 0.8, 0.25] },
        { type: 'cube', name: 'arm R', pos: [0.6, 0.05, 0], color: '#2476D6', scale: [0.25, 0.8, 0.25] },
      ]),
  },
  {
    id: 'robot',
    label: 'Robot',
    build: () =>
      assemble('Robot', [0, 0.7, 0], [
        { type: 'cube', name: 'body', pos: [0, 0, 0], color: '#8A93A6', scale: [0.9, 1.2, 0.6] },
        { type: 'cube', name: 'head', pos: [0, 1.0, 0], color: '#B7C0D0', scale: [0.7, 0.6, 0.6] },
        { type: 'sphere', name: 'eye L', pos: [-0.18, 1.05, 0.32], color: '#2BE0FF', scale: [0.15, 0.15, 0.15] },
        { type: 'sphere', name: 'eye R', pos: [0.18, 1.05, 0.32], color: '#2BE0FF', scale: [0.15, 0.15, 0.15] },
        { type: 'cube', name: 'arm L', pos: [-0.7, 0.1, 0], color: '#6B7488', scale: [0.25, 0.9, 0.25] },
        { type: 'cube', name: 'arm R', pos: [0.7, 0.1, 0], color: '#6B7488', scale: [0.25, 0.9, 0.25] },
      ]),
  },
  {
    id: 'tree',
    label: 'Tree',
    build: () =>
      assemble('Tree', [0, 0, 0], [
        { type: 'cube', name: 'trunk', pos: [0, 0.8, 0], color: '#6B4A2B', scale: [0.35, 1.6, 0.35] },
        { type: 'sphere', name: 'canopy', pos: [0, 2.1, 0], color: '#43C46A', scale: [1.5, 1.6, 1.5] },
        { type: 'sphere', name: 'canopy top', pos: [0, 2.9, 0], color: '#4FD37A', scale: [1.0, 1.0, 1.0] },
      ]),
  },
];

export function instantiateFigure(id: string): SceneObject[] | null {
  const fig = FIGURES.find((f) => f.id === id);
  return fig ? fig.build() : null;
}
